/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * UI domain - Leaderboard system with React-based rendering for consistent state
 * leaderboard.ui.client.tsx - React-powered leaderboard component
 */

import { useGameState } from "@game/state";
import type { Player } from "../../../../packages/protocol/src/generated/messages";
import { useEffect, useState } from "react";
import { useLeaderboardStore } from "./leaderboard.store";

/**
 * Format large numbers with K/M/B suffix
 * Examples: 1234 → 1.2K, 1500000 → 1.5M
 */
function formatNumber(num: number): string {
  if (num < 1000) return num.toString();
  if (num < 1000000) return `${(num / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  if (num < 1000000000)
    return `${(num / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  return `${(num / 1000000000).toFixed(1).replace(/\.0$/, "")}B`;
}

// Type definitions for leaderboard component
export interface LeaderboardPlayer {
  id: string;
  name: string;
  color?: string;
  pelletsCollected: number;
  kills: number;
}

export interface LobbyPlayer {
  id: string;
  name: string;
  color?: string;
  ready: boolean;
}

export interface LeaderboardConfig {
  containerSelector: string;
  contentSelector: string;
  animationDuration: number;
  enableColorHighlights: boolean;
  enableStickyPlayer: boolean;
}

/**
 * Team data structure
 */
interface TeamData {
  id: number;
  name: string;
  color: string;
  bgColor: string;
  kills: number;
  pellets: number;
  captures: number;
  players: number;
  alive: number;
}

/**
 * Player entry data structure
 */
interface PlayerEntryData {
  player: Player & { id: string };
  rank: number;
  isGame: boolean;
}

/**
 * Team entry component
 */
function TeamEntry({
  team,
  rank,
  isCTF,
}: {
  team: TeamData;
  rank: number;
  isCTF: boolean;
}) {
  return (
    <div
      className="leaderboard-entry"
      style={{ backgroundColor: team.bgColor }}
    >
      <div className="leaderboard-rank">{rank}</div>
      <div
        className="leaderboard-name"
        style={{ color: team.color, fontWeight: "bold" }}
      >
        {team.name} ({team.alive}/{team.players})
      </div>
      <div className="leaderboard-stats">
        {isCTF ? (
          <>
            <div className="leaderboard-captures">
              <span className="leaderboard-icon">🚩</span>
              {team.captures}
            </div>
            <div className="leaderboard-kills">
              <span className="leaderboard-icon">⚔</span>
              {formatNumber(team.kills)}
            </div>
          </>
        ) : (
          <>
            <div className="leaderboard-pellets">
              <span className="leaderboard-icon">◆</span>
              {formatNumber(team.pellets)}
            </div>
            <div className="leaderboard-kills">
              <span className="leaderboard-icon">⚔</span>
              {formatNumber(team.kills)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Player entry component
 */
function PlayerEntry({
  playerData,
  isCurrentPlayer,
  enableStickyPlayer,
  isCTF,
}: {
  playerData: PlayerEntryData;
  isCurrentPlayer: boolean;
  enableStickyPlayer: boolean;
  isCTF: boolean;
}) {
  const { player, rank, isGame } = playerData;

  const rankColor =
    rank === 1
      ? "#FFD700"
      : rank === 2
        ? "#C0C0C0"
        : rank === 3
          ? "#CD7F32"
          : "";

  const stickyStyle =
    isCurrentPlayer && enableStickyPlayer
      ? {
          backgroundColor: "#131415",
          border: "0.5px solid rgba(255, 255, 255, 0.4)",
          position: "sticky" as const,
          top: "-3.5px",
          bottom: "-3.5px",
          zIndex: 10,
        }
      : {};

  return (
    <div className="leaderboard-entry" style={stickyStyle}>
      <div className="leaderboard-rank" style={{ color: rankColor }}>
        {rank}
      </div>
      <div
        className="leaderboard-name"
        style={{
          fontWeight: isCurrentPlayer ? "bold" : "normal",
          color: player.color || "",
        }}
      >
        {player.name || `Player ${player.id.slice(-4)}`}
      </div>
      <div
        className="leaderboard-stats"
        style={{ display: isGame ? "flex" : "none" }}
      >
        {isGame && (
          <>
            {isCTF ? (
              <>
                <div className="leaderboard-captures">
                  <span className="leaderboard-icon">🚩</span>
                  {player.captures ?? 0}
                </div>
                <div className="leaderboard-kills">
                  <span className="leaderboard-icon">⚔</span>
                  {formatNumber(player.kills)}
                </div>
              </>
            ) : (
              <>
                <div className="leaderboard-pellets">
                  <span className="leaderboard-icon">◆</span>
                  {formatNumber(player.pelletsCollected)}
                </div>
                <div className="leaderboard-kills">
                  <span className="leaderboard-icon">⚔</span>
                  {formatNumber(player.kills)}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Detects if the device is mobile based on viewport width
 * Uses 900px breakpoint to include landscape phones and tablets
 * - Portrait phones: 360-428px
 * - Landscape phones: 667-926px
 * - Small tablets: 768-834px
 */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 900px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(max-width: 900px)");
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  return isMobile;
}

/**
 * React component for the leaderboard
 */
// biome-ignore lint/complexity/noExcessiveLinesPerFunction: Display component with mobile/desktop conditional rendering
export function LeaderboardComponent() {
  // Zustand subscriptions for reactive updates
  const players = useGameState((state) => state.players);
  const playerId = useGameState((state) => state.playerId);
  const gameStarted = useGameState((state) => state.gameStarted);
  const lobbyId = useGameState((state) => state.lobbyId);
  const lobbyTeamMode = useGameState((state) => state.lobbyTeamMode);

  // Leaderboard store state
  const visible = useLeaderboardStore((state) => state.visible);
  const leaderboardConfig = useLeaderboardStore((state) => state.config);
  const setMaxPlayers = useLeaderboardStore((state) => state.setMaxPlayers);
  const resetMaxPlayers = useLeaderboardStore((state) => state.resetMaxPlayers);
  const maxPlayersFromStore = useLeaderboardStore((state) => state.maxPlayers);

  // React state for maxPlayers tracking
  const [maxPlayers, setMaxPlayersLocal] = useState<number | null>(null);

  // Mobile detection and collapse state
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isHolding, setIsHolding] = useState<boolean>(false);

  // Update maxPlayers when game starts
  useEffect(() => {
    if (gameStarted && maxPlayers === null && players.size > 0) {
      const newMax = Math.max(players.size, 1);
      setMaxPlayersLocal(newMax);
      setMaxPlayers(newMax);
    }
  }, [gameStarted, players.size, maxPlayers, setMaxPlayers]);

  // Reset maxPlayers when back in lobby (new game)
  useEffect(() => {
    if (!gameStarted && players.size === 0) {
      setMaxPlayersLocal(null);
      resetMaxPlayers();
    }
  }, [gameStarted, players.size, resetMaxPlayers]);

  // Visibility logic
  const shouldShow = lobbyId !== null || gameStarted;

  // Return null if not visible
  if (!visible || !shouldShow) {
    return null;
  }

  // Check if we're in teams or CTF mode from lobby metadata
  const hasTeams = lobbyTeamMode === "teams" || lobbyTeamMode === "ctf";

  // Calculate team scores if in teams mode (show in both lobby and game)
  let teams: TeamData[] = [];
  if (hasTeams) {
    const teamScores = {
      0: { kills: 0, pellets: 0, captures: 0, players: 0, alive: 0 },
      1: { kills: 0, pellets: 0, captures: 0, players: 0, alive: 0 },
    };

    players.forEach((player) => {
      if (player.teamId === 0 || player.teamId === 1) {
        teamScores[player.teamId].kills += player.kills;
        teamScores[player.teamId].pellets += player.pelletsCollected;
        teamScores[player.teamId].captures += player.captures ?? 0;
        teamScores[player.teamId].players++;
        if (player.health > 0) {
          teamScores[player.teamId].alive++;
        }
      }
    });

    // Check if CTF mode for sorting
    const isCTFMode = lobbyTeamMode === "ctf";

    teams = [
      {
        id: 0,
        name: "TEAM BLUE",
        color: "#60a5fa",
        bgColor: "rgba(59, 130, 246, 0.05)",
        ...teamScores[0],
      },
      {
        id: 1,
        name: "TEAM RED",
        color: "#f87171",
        bgColor: "rgba(239, 68, 68, 0.05)",
        ...teamScores[1],
      },
    ].sort((a, b) => {
      if (isCTFMode) {
        // CTF: Sort by captures first, then kills
        if (b.captures !== a.captures) {
          return b.captures - a.captures;
        }
        return b.kills - a.kills;
      }
      // Teams: Sort by kills * 10 + pellets
      const scoreA = a.kills * 10 + a.pellets;
      const scoreB = b.kills * 10 + b.pellets;
      return scoreB - scoreA;
    });
  }

  // Check if CTF mode for sorting and display
  const isCTF = lobbyTeamMode === "ctf";

  // Sort players by score (CTF: captures first, then kills; FFA/Teams: pellets first, then kills)
  const sortedPlayers: PlayerEntryData[] = Array.from(players.entries())
    .sort(([, a], [, b]) => {
      if (isCTF) {
        // CTF: Sort by captures first, then kills
        const capturesA = a.captures ?? 0;
        const capturesB = b.captures ?? 0;
        if (capturesB !== capturesA) {
          return capturesB - capturesA;
        }
        return b.kills - a.kills;
      }
      // FFA/Teams: Sort by pellets first, then kills
      if (b.pelletsCollected !== a.pelletsCollected) {
        return b.pelletsCollected - a.pelletsCollected;
      }
      return b.kills - a.kills;
    })
    .map(([id, player], index) => ({
      player: { ...player, id },
      rank: index + 1,
      isGame: true,
    }));

  // Calculate player count display
  const currentCount = sortedPlayers.length;
  const countText =
    maxPlayersFromStore !== null && gameStarted
      ? `${currentCount}/${maxPlayersFromStore}`
      : `${currentCount}`;

  // Display logic: show top 10 + current player if not in top 10
  const displayPlayers = (() => {
    const topPlayers = sortedPlayers.slice(0, 10);
    const currentPlayerIndex = sortedPlayers.findIndex(
      (p) => p.player.id === playerId,
    );

    // If current player is in top 10 or doesn't exist, just show top 10
    if (currentPlayerIndex === -1 || currentPlayerIndex < 10) {
      return topPlayers;
    }

    // Otherwise, include current player at the end
    const currentPlayerData = sortedPlayers[currentPlayerIndex];
    if (!currentPlayerData) {
      return topPlayers;
    }
    return [...topPlayers, currentPlayerData];
  })();

  // Handle tap-and-hold for mobile
  const handlePointerDown = (e: React.PointerEvent) => {
    if (isMobile) {
      e.preventDefault();
      setIsHolding(true);
      setIsExpanded(true);
    }
  };

  const handlePointerUp = () => {
    if (isMobile) {
      setIsHolding(false);
      setIsExpanded(false);
    }
  };

  // Determine if content should be shown
  const showContent = !isMobile || isExpanded;

  // Mobile collapsed button
  if (isMobile && !isExpanded) {
    return (
      <button
        type="button"
        id="leaderboard-mobile-toggle"
        className="leaderboard-mobile-toggle"
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          position: "fixed",
          top: "40px",
          left: "16px",
          padding: "8px 12px",
          background: isHolding ? "rgba(0, 0, 0, 0.9)" : "var(--bg)",
          border: "0.5px solid var(--warning)",
          borderRadius: "4px",
          color: "var(--warning)",
          fontSize: "16px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "1.5px",
          cursor: "pointer",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          gap: "8px",
          opacity: isHolding ? 1 : 0.65,
          transition: "opacity 0.1s, background 0.1s",
          touchAction: "none",
        }}
      >
        {/* Nerd Font icon: \uf091 (fa-trophy) from JetBrainsMono Nerd Font */}
        <span style={{ fontSize: "20px" }}>{"\uf091"}</span>
        <span>{countText}</span>
      </button>
    );
  }

  return (
    <div
      id="leaderboard"
      className={`show ${isMobile ? "mobile-expanded" : ""}`}
      style={{
        display: "block",
        opacity: 0.65,
        ...(isMobile && {
          position: "fixed",
          top: "40px",
          left: "16px",
          right: "auto",
          width: "280px",
          animation: "leaderboard-slide-in 0.3s ease-out",
        }),
      }}
    >
      <div
        className="leaderboard-title"
        style={{
          color: "var(--warning)",
        }}
      >
        <div className="title-group">
          {/* Nerd Font icon: \uf091 (fa-trophy) from JetBrainsMono Nerd Font */}
          <span className="trophy-icon" style={{ fontSize: "20px" }}>
            {"\uf091"}
          </span>
          <span className="title-text">LEADERBOARD</span>
        </div>
        <span className="count-text">{countText}</span>
      </div>
      {showContent && (
        <div className="leaderboard-content">
          {teams.length > 0 && (
            <>
              {teams.map((team, index) => (
                <TeamEntry
                  key={team.id}
                  team={team}
                  rank={index + 1}
                  isCTF={isCTF}
                />
              ))}
              <hr style={{ borderColor: "var(--border)", margin: "8px 0" }} />
            </>
          )}
          {displayPlayers.map((playerData) => (
            <PlayerEntry
              key={playerData.player.id}
              playerData={playerData}
              isCurrentPlayer={playerData.player.id === playerId}
              enableStickyPlayer={leaderboardConfig.enableStickyPlayer}
              isCTF={isCTF}
            />
          ))}
        </div>
      )}
    </div>
  );
}
